import {Component, inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DatePicker, Select} from '@core/components';
import {TaskItem} from '@core/types';
import {ModalService, TaskService} from '@core/services';

@Component({
  selector: 'core-modal-task-form',
  imports: [
    ReactiveFormsModule,
    DatePicker,
    Select
  ],
  templateUrl: './task-form.html',
  styleUrl: './task-form.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class TaskForm implements OnInit {
  @Input() task: TaskItem | null = null;
  @Input() assigneeOptions: {label: string; value: string | number}[] = [];
  @Input() statusOptions: {label: string; value: string}[] = [];
  @Input() priorityOptions: {label: string; value: string}[] = [];
  form: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  isEditMode = false;
  private modal = inject(ModalService);
  private taskService = inject(TaskService);
  private formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      dateRange: [null],
      status: ['open'],
      priority: ['medium'],
      assigneeId: [null]
    });
  }

  ngOnInit() {
    if (!this.task?.id) return;
    this.isEditMode = true;
    this.form.patchValue({
      name: this.task.name || '',
      description: this.task.description || '',
      dateRange: this.task.startDate && this.task.endDate ? {
        start: this.task.startDate,
        end: this.task.endDate
      } : null,
      status: this.task.status || 'open',
      priority: this.task.priority || 'medium',
      assigneeId: this.task.assignedTo?.id || this.task.assignees?.[0]?.id || null
    });
  }

  async submit() {
    if (this.isSubmitting) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const value = this.form.value;
    const payload = {
      name: value.name,
      description: value.description || undefined,
      status: value.status || undefined,
      priority: value.priority || undefined,
      startDate: value.dateRange?.start || null,
      endDate: value.dateRange?.end || null
    };

    const response = this.isEditMode && this.task?.id
      ? await this.taskService.updateTask(this.task.id, payload, {skipLoader: true})
      : await this.taskService.createTask(payload, {skipLoader: true});

    if (!response.success || !response.result) {
      this.isSubmitting = false;
      this.errorMessage = response.message || (this.isEditMode ? 'No se pudo actualizar la tarea.' : 'No se pudo crear la tarea.');
      return;
    }

    const assigneeId = value.assigneeId;
    if (assigneeId) {
      const assignResponse = await this.taskService.setAssignees(response.result.id, [assigneeId], {skipLoader: true});
      if (!assignResponse.success || !assignResponse.result) {
        this.isSubmitting = false;
        this.errorMessage = assignResponse.message || 'No se pudieron asignar responsables.';
        return;
      }
      this.isSubmitting = false;
      this.modal.closeTop(assignResponse.result);
      return;
    }

    this.isSubmitting = false;
    this.modal.closeTop(response.result);
  }
}
